import { type JSX, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { cn } from "@/libs/cn";
import { categoryIconClass } from "../../constants/category-icons";
import type { SectorDraft, SlotIndex } from "../../selector/types";
import { categoryLabel } from "../../store/taxonomy-store";
import { SuggestedLegend } from "./legend";
import { FOCUS_RING, IconButton, SlotBadge } from "./primitives";
import { SubRows } from "./sub-rows";

const I18N = "taxonomy.sector" as const;

/**
 * A chosen sector with its subcategories: icon, title, badge and commands
 * ("Rendi principale" on the secondary, the trash when the sector can go),
 * then the option rows and the legend. `children` goes below, for what the
 * surface puts next to the sector.
 *
 * Below `@md` the commands wrap under the title, in a line of their own; the
 * title can take two lines at 320 px with the longest label (AC31). The
 * scroll and the focus after an action are the selector's: the section only
 * hands out the refs and the callbacks.
 */
export const SlotSection = (props: {
  draft: SectorDraft;
  index: SlotIndex;
  headingId: string;
  /** The title, the element that takes the focus (`tabindex="-1"`). */
  headingRef?: (el: HTMLHeadingElement) => void;
  sectionRef?: (el: HTMLElement) => void;
  /** After the sector was taken off (its trash). */
  onRemoved?: (index: SlotIndex) => void;
  /** After «Rendi principale». */
  onSwapped?: () => void;
  children?: JSX.Element;
}) => {
  const { t } = useI18n();
  const slot = () => props.draft.slots[props.index];

  return (
    <Show when={slot()}>
      {(current) => (
        <section
          ref={(el) => props.sectionRef?.(el)}
          aria-labelledby={props.headingId}
          class="flex scroll-mt-6 flex-col gap-3"
        >
          <div class="flex flex-wrap items-center gap-x-3 gap-y-2 @md:flex-nowrap">
            <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <i
                class={cn("ph text-lg", categoryIconClass(current().category))}
                aria-hidden="true"
              />
            </span>
            <div class="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
              <h3
                id={props.headingId}
                ref={(el) => props.headingRef?.(el)}
                tabindex="-1"
                class="min-w-0 break-words text-sm font-semibold outline-hidden"
              >
                {categoryLabel(current().category)}
              </h3>
              <SlotBadge index={props.index} />
            </div>
            <div class="flex w-full shrink-0 items-center gap-1 pl-12 @md:w-auto @md:pl-0">
              <Show when={props.index === 1}>
                <Button
                  variant="ghost"
                  size="sm"
                  class={cn("h-7 px-2 text-xs", FOCUS_RING)}
                  onClick={() => {
                    props.draft.swap();
                    props.onSwapped?.();
                  }}
                >
                  {t(`${I18N}.makePrimary`)}
                </Button>
              </Show>
              <Show when={props.draft.canRemove(props.index)}>
                <IconButton
                  icon="ph-trash"
                  label={
                    props.index === 0
                      ? t(`${I18N}.remove.primary`)
                      : t(`${I18N}.remove.secondary`)
                  }
                  onClick={() => {
                    props.draft.removeCategory(props.index);
                    props.onRemoved?.(props.index);
                  }}
                />
              </Show>
            </div>
          </div>
          <SubRows
            draft={props.draft}
            index={props.index}
            labelledBy={props.headingId}
          />
          <SuggestedLegend draft={props.draft} index={props.index} />
          {props.children}
        </section>
      )}
    </Show>
  );
};
